import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Settings,
  Users,
  UserPlus,
  Copy,
  Check,
  Trash2,
  ExternalLink,
  Eye,
  EyeOff,
  Circle,
  CircleDot,
} from 'lucide-react';
import { get, post, patch, del } from '@/shared/api/api_client';
import { useAuth } from '@/features/auth';
import { Navbar } from '@/widgets/Navbar';
import { Button, Card, Input, Modal, Badge } from '@/shared/ui';
import AddFamilyMember from '@/features/family/AddFamilyMember';
import { formatDate } from '@/shared/utils/format';
import type { Family, Invitation } from '@/entities/family';
import type { FamilyMember, Sex } from '@/entities/user';
import { cn } from '@/shared/utils/cn';

// ---------------------------------------------------------------------------
// Response types
// ---------------------------------------------------------------------------
interface FamilyResponse extends Family {}

interface FamilyMembersResponse {
  members: FamilyMember[];
}

interface InvitationsResponse {
  invitations: Invitation[];
}

// ---------------------------------------------------------------------------
// Rename family schema
// ---------------------------------------------------------------------------
const renameFamilySchema = z.object({
  name: z.string().min(2, 'At least 2 characters').max(50, 'Max 50 characters'),
});

type RenameFamilyValues = z.infer<typeof renameFamilySchema>;

// ---------------------------------------------------------------------------
// Enable login schema (inline modal)
// ---------------------------------------------------------------------------
const enableLoginSchema = z
  .object({
    username: z
      .string()
      .min(3, 'At least 3 characters')
      .max(30, '30 characters max')
      .regex(/^[a-zA-Z0-9_]+$/, 'Letters, numbers, underscores only'),
    pin: z
      .string()
      .min(4, '4–6 digits required')
      .max(6, '4–6 digits required')
      .regex(/^\d+$/, 'Digits only'),
    confirmPin: z.string().min(1, 'Please confirm PIN'),
    canChangePin: z.boolean(),
  })
  .refine((d) => d.pin === d.confirmPin, {
    message: 'PINs do not match',
    path: ['confirmPin'],
  });

type EnableLoginValues = z.infer<typeof enableLoginSchema>;

// ---------------------------------------------------------------------------
// Age calculation helper
// ---------------------------------------------------------------------------
function calcAge(dob: string): number | null {
  const birth = new Date(dob);
  if (isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}

// ---------------------------------------------------------------------------
// Sex icon helper
// ---------------------------------------------------------------------------
function SexIcon({ sex }: { sex: Sex | null }): React.ReactElement | null {
  if (!sex || sex === 'prefer_not_to_say') return null;
  if (sex === 'male') {
    return <Circle size={13} className="text-blue-400 shrink-0 fill-blue-400" aria-label="Male" />;
  }
  if (sex === 'female') {
    return <Circle size={13} className="text-pink-400 shrink-0 fill-pink-400" aria-label="Female" />;
  }
  return <CircleDot size={13} className="text-slate-400 shrink-0" aria-label="Other" />;
}

// ---------------------------------------------------------------------------
// Copy invite link button
// ---------------------------------------------------------------------------
function CopyLinkButton({ link }: { link: string }): React.ReactElement {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard.writeText(link).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        });
      }}
      className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors duration-150"
      aria-label="Copy invite link"
    >
      {copied ? <Check size={14} className="text-secondary-600" /> : <Copy size={14} />}
      {copied ? 'Copied!' : 'Copy link'}
    </button>
  );
}

// ---------------------------------------------------------------------------
// EnableLoginModal — inline component
// ---------------------------------------------------------------------------
interface EnableLoginModalProps {
  familyId: string;
  member: FamilyMember;
  onSuccess: () => void;
  onClose: () => void;
}

function EnableLoginModal({
  familyId,
  member,
  onSuccess,
  onClose,
}: EnableLoginModalProps): React.ReactElement {
  const queryClient = useQueryClient();
  const [showPin, setShowPin] = useState(false);
  const [showConfirmPin, setShowConfirmPin] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<EnableLoginValues>({
    resolver: zodResolver(enableLoginSchema),
    defaultValues: {
      username: '',
      pin: '',
      confirmPin: '',
      canChangePin: true,
    },
  });

  const mutation = useMutation({
    mutationFn: (data: EnableLoginValues) =>
      post<void>(`/auth/members/${member.memberId}/enable-login`, {
        username: data.username,
        pin: data.pin,
        canChangePin: data.canChangePin,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['members', familyId] });
      onSuccess();
    },
  });

  return (
    <Modal isOpen onClose={onClose} title={`Enable Login — ${member.displayName}`} size="sm">
      <form
        onSubmit={handleSubmit((data) => mutation.mutate(data))}
        noValidate
        className="flex flex-col gap-4"
      >
        {/* Username */}
        <Input
          label="Username"
          placeholder="e.g. alex_smith"
          hint="3–30 characters, letters/numbers/underscores"
          error={errors.username?.message}
          autoComplete="off"
          {...register('username')}
        />

        {/* PIN */}
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-slate-700">PIN</label>
          <div className="relative">
            <input
              type={showPin ? 'text' : 'password'}
              inputMode="numeric"
              maxLength={6}
              placeholder="••••••"
              autoComplete="new-password"
              aria-invalid={errors.pin ? 'true' : undefined}
              className={cn(
                'block w-full rounded-lg border bg-white px-3 py-2 pr-10 text-xl tracking-[0.4em] text-slate-900 placeholder:text-slate-300 placeholder:tracking-normal',
                'focus:outline-none focus:ring-2 focus:ring-offset-0 transition-colors duration-150',
                errors.pin
                  ? 'border-danger-400 focus:ring-danger-400'
                  : 'border-slate-300 focus:ring-primary-500 focus:border-primary-500',
              )}
              {...register('pin')}
            />
            <button
              type="button"
              aria-label={showPin ? 'Hide PIN' : 'Show PIN'}
              onClick={() => setShowPin((v) => !v)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600"
            >
              {showPin ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
            </button>
          </div>
          <p className="text-xs text-slate-500">4–6 digits</p>
          {errors.pin && (
            <p role="alert" className="text-xs text-danger-600">
              {errors.pin.message}
            </p>
          )}
        </div>

        {/* Confirm PIN */}
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-slate-700">Confirm PIN</label>
          <div className="relative">
            <input
              type={showConfirmPin ? 'text' : 'password'}
              inputMode="numeric"
              maxLength={6}
              placeholder="••••••"
              autoComplete="new-password"
              aria-invalid={errors.confirmPin ? 'true' : undefined}
              className={cn(
                'block w-full rounded-lg border bg-white px-3 py-2 pr-10 text-xl tracking-[0.4em] text-slate-900 placeholder:text-slate-300 placeholder:tracking-normal',
                'focus:outline-none focus:ring-2 focus:ring-offset-0 transition-colors duration-150',
                errors.confirmPin
                  ? 'border-danger-400 focus:ring-danger-400'
                  : 'border-slate-300 focus:ring-primary-500 focus:border-primary-500',
              )}
              {...register('confirmPin')}
            />
            <button
              type="button"
              aria-label={showConfirmPin ? 'Hide confirm PIN' : 'Show confirm PIN'}
              onClick={() => setShowConfirmPin((v) => !v)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600"
            >
              {showConfirmPin ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
            </button>
          </div>
          {errors.confirmPin && (
            <p role="alert" className="text-xs text-danger-600">
              {errors.confirmPin.message}
            </p>
          )}
        </div>

        {/* Can change PIN */}
        <Controller
          control={control}
          name="canChangePin"
          render={({ field }) => (
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={field.value}
                onChange={field.onChange}
                className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-slate-700">
                Allow child to change their own PIN
              </span>
            </label>
          )}
        />

        {mutation.isError && (
          <p role="alert" className="rounded-lg bg-danger-50 px-3 py-2 text-sm text-danger-700">
            {mutation.error instanceof Error
              ? mutation.error.message
              : 'Failed to enable login. Please try again.'}
          </p>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={mutation.isPending}>
            Enable Login
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// MemberCard
// ---------------------------------------------------------------------------
interface MemberCardProps {
  member: FamilyMember;
  currentUserMemberId: string | null;
  onRemove: (memberId: string) => void;
  onEnableLogin: (member: FamilyMember) => void;
  onDisableLogin: (member: FamilyMember) => void;
}

function MemberCard({
  member,
  currentUserMemberId,
  onRemove,
  onEnableLogin,
  onDisableLogin,
}: MemberCardProps): React.ReactElement {
  const age = member.dateOfBirth ? calcAge(member.dateOfBirth) : null;
  const isSelf = member.memberId === currentUserMemberId;

  return (
    <div
      className="flex items-start justify-between gap-3 py-4"
      role="listitem"
    >
      {/* Avatar + info */}
      <div className="flex items-start gap-3 min-w-0">
        <div
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold',
            member.role === 'child'
              ? 'bg-secondary-100 text-secondary-700'
              : 'bg-primary-100 text-primary-700',
          )}
        >
          {member.displayName.charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-semibold text-slate-900 truncate">
              {member.displayName}
            </p>
            <SexIcon sex={member.sex} />
          </div>

          {/* Role badge */}
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
            <Badge variant={member.role === 'parent' ? 'info' : 'success'}>
              {member.isPrimary
                ? 'Primary Parent'
                : member.role.charAt(0).toUpperCase() + member.role.slice(1)}
            </Badge>

            {/* Age */}
            {age !== null && (
              <span className="text-xs text-slate-500">Age {age}</span>
            )}

            {/* Login status for children */}
            {member.role === 'child' && (
              member.hasPin || member.username ? (
                <Badge variant="success">Can log in</Badge>
              ) : (
                <Badge variant="neutral">No login</Badge>
              )
            )}
          </div>

          {/* Username */}
          {member.username && (
            <p className="mt-0.5 text-xs text-slate-400">@{member.username}</p>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex shrink-0 items-center gap-1.5">
        {/* Child login actions */}
        {member.role === 'child' && (
          member.hasPin || member.username ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onDisableLogin(member)}
              className="text-xs text-slate-500 hover:text-danger-600"
            >
              Disable Login
            </Button>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onEnableLogin(member)}
              className="text-xs"
            >
              Enable Login
            </Button>
          )
        )}

        {/* Remove button (not for self or primary) */}
        {!member.isPrimary && !isSelf && (
          <button
            type="button"
            onClick={() => onRemove(member.memberId)}
            className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-danger-50 hover:text-danger-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger-500 transition-colors duration-150"
            aria-label={`Remove ${member.displayName}`}
          >
            <Trash2 size={14} aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FamilySettingsPage
// ---------------------------------------------------------------------------
export default function FamilySettingsPage(): React.ReactElement {
  const { appUser } = useAuth();
  const queryClient = useQueryClient();
  const familyId = appUser?.familyId ?? '';
  const currentUserMemberId = appUser?.memberId ?? null;

  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [newInviteLink, setNewInviteLink] = useState<string | null>(null);
  const [removeMemberId, setRemoveMemberId] = useState<string | null>(null);
  const [enableLoginMember, setEnableLoginMember] = useState<FamilyMember | null>(null);
  const [disableLoginMember, setDisableLoginMember] = useState<FamilyMember | null>(null);

  // ---------------------------------------------------------------------------
  // Queries
  // ---------------------------------------------------------------------------
  const { data: familyData, isLoading: familyLoading } = useQuery<FamilyResponse>({
    queryKey: ['family', familyId],
    queryFn: () => get<FamilyResponse>(`/families/${familyId}`),
    enabled: Boolean(familyId),
  });

  const { data: membersData, isLoading: membersLoading } = useQuery<FamilyMembersResponse>({
    queryKey: ['members', familyId],
    queryFn: () => get<FamilyMembersResponse>(`/families/${familyId}/members`),
    enabled: Boolean(familyId),
  });

  const { data: invitationsData } = useQuery<InvitationsResponse>({
    queryKey: ['invitations', familyId],
    queryFn: () => get<InvitationsResponse>(`/families/${familyId}/invitations`),
    enabled: Boolean(familyId),
  });

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------
  const renameFamilyMutation = useMutation({
    mutationFn: (name: string) => patch<void>(`/families/${familyId}`, { name }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['family', familyId] }),
  });

  const removeMemberMutation = useMutation({
    mutationFn: (memberId: string) =>
      del<void>(`/families/${familyId}/members/${memberId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['members', familyId] });
      setRemoveMemberId(null);
    },
  });

  const disableLoginMutation = useMutation({
    mutationFn: (memberId: string) =>
      post<void>(`/auth/members/${memberId}/disable-login`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['members', familyId] });
      setDisableLoginMember(null);
    },
  });

  const revokeInviteMutation = useMutation({
    mutationFn: (inviteId: string) =>
      patch<void>(`/families/${familyId}/invitations/${inviteId}/revoke`),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ['invitations', familyId] }),
  });

  // ---------------------------------------------------------------------------
  // Rename form
  // ---------------------------------------------------------------------------
  const {
    register: registerRename,
    handleSubmit: handleRenameSubmit,
    formState: { errors: renameErrors, isSubmitting: renameSubmitting },
  } = useForm<RenameFamilyValues>({
    resolver: zodResolver(renameFamilySchema),
    defaultValues: { name: familyData?.name ?? '' },
  });

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------
  const handleGenerateInvite = async () => {
    setInviteLoading(true);
    setNewInviteLink(null);
    try {
      const res = await post<{ shareableLink: string }>(
        `/families/${familyId}/invitations`,
      );
      setNewInviteLink(res.shareableLink);
      void queryClient.invalidateQueries({ queryKey: ['invitations', familyId] });
    } finally {
      setInviteLoading(false);
    }
  };

  const members = membersData?.members ?? [];
  const invitations = invitationsData?.invitations ?? [];
  const pendingInvites = invitations.filter((i) => i.status === 'pending');
  const family = familyData;
  const atLimit = family ? family.memberCount >= family.memberLimit : false;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <main className="mx-auto max-w-3xl px-4 py-6 sm:px-6 space-y-6">
        <div className="flex items-center gap-2">
          <Settings size={22} className="text-primary-600" aria-hidden="true" />
          <h1 className="text-xl font-bold text-slate-900">Family Settings</h1>
        </div>

        {/* 1. Family name */}
        <Card>
          <h2 className="mb-4 text-base font-semibold text-slate-800">Family Name</h2>
          {familyLoading ? (
            <div className="animate-pulse h-10 w-full rounded-lg bg-slate-100" />
          ) : (
            <form
              onSubmit={handleRenameSubmit((data) =>
                renameFamilyMutation.mutate(data.name),
              )}
              noValidate
              className="flex gap-3"
            >
              <Input
                defaultValue={family?.name}
                placeholder="Family name"
                error={renameErrors.name?.message}
                {...registerRename('name')}
              />
              <Button type="submit" isLoading={renameSubmitting}>
                Save
              </Button>
            </form>
          )}
        </Card>

        {/* 2. Members */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-slate-800 flex items-center gap-2">
              <Users size={18} aria-hidden="true" />
              Members
              {family && (
                <span className="text-sm font-normal text-slate-500">
                  ({family.memberCount}/{family.memberLimit})
                </span>
              )}
            </h2>
            {!atLimit && (
              <Button
                size="sm"
                onClick={() => setShowAddMemberModal(true)}
                leftIcon={<UserPlus size={14} />}
              >
                Add Family Member
              </Button>
            )}
          </div>

          {membersLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse h-16 rounded-lg bg-slate-50" />
              ))}
            </div>
          ) : (
            <div
              className="divide-y divide-slate-100"
              role="list"
              aria-label="Family members"
            >
              {members.map((member) => (
                <MemberCard
                  key={member.memberId}
                  member={member}
                  currentUserMemberId={currentUserMemberId}
                  onRemove={(id) => setRemoveMemberId(id)}
                  onEnableLogin={(m) => setEnableLoginMember(m)}
                  onDisableLogin={(m) => setDisableLoginMember(m)}
                />
              ))}
            </div>
          )}

          {atLimit && (
            <p className="mt-3 text-xs text-slate-500">
              You've reached your member limit.{' '}
              <button
                type="button"
                className="text-primary-600 hover:text-primary-800 font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary-500 rounded"
                onClick={() => {
                  /* TODO: open upgrade flow */
                }}
              >
                Request more members
              </button>
            </p>
          )}
        </Card>

        {/* 3. Invite a co-parent */}
        <Card>
          <h2 className="mb-3 text-base font-semibold text-slate-800">
            Invite a Co-Parent
          </h2>
          <p className="text-sm text-slate-500 mb-4">
            Generate a shareable invite link. The link expires in 7 days.
          </p>

          {newInviteLink ? (
            <div className="flex flex-col gap-3">
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700 break-all">
                {newInviteLink}
              </div>
              <div className="flex gap-2">
                <CopyLinkButton link={newInviteLink} />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setNewInviteLink(null)}
                >
                  Done
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="secondary"
              leftIcon={<UserPlus size={16} />}
              isLoading={inviteLoading}
              onClick={() => void handleGenerateInvite()}
            >
              Generate Invite Link
            </Button>
          )}

          {/* Pending invites */}
          {pendingInvites.length > 0 && (
            <div className="mt-5">
              <h3 className="text-sm font-semibold text-slate-700 mb-2">
                Pending Invitations
              </h3>
              <div className="space-y-2">
                {pendingInvites.map((invite) => (
                  <div
                    key={invite.id}
                    className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2"
                  >
                    <span className="text-xs text-slate-600">
                      Expires {formatDate(invite.expiresAt)}
                    </span>
                    <button
                      type="button"
                      onClick={() => revokeInviteMutation.mutate(invite.id)}
                      className="text-xs text-danger-600 hover:text-danger-800 font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-danger-500 rounded"
                    >
                      Revoke
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>

        {/* 4. Subscription */}
        <Card>
          <h2 className="mb-2 text-base font-semibold text-slate-800">Subscription</h2>
          <p className="text-sm text-slate-500 mb-3">
            Manage your KidKred subscription, billing, and plan.
          </p>
          <a
            href="/billing"
            className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-800 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
          >
            Go to Billing
            <ExternalLink size={13} aria-hidden="true" />
          </a>
        </Card>

        {/* 5. MCP section */}
        <Card>
          <h2 className="mb-2 text-base font-semibold text-slate-800">
            MCP Integration
          </h2>
          <p className="text-sm text-slate-500 mb-3">
            Use the KidKred MCP API to manage chores and rewards from AI assistants.
          </p>
          <a
            href="/account"
            className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-800 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
          >
            Manage MCP Tokens in Account Settings
            <ExternalLink size={13} aria-hidden="true" />
          </a>
        </Card>
      </main>

      {/* Add Family Member modal */}
      {showAddMemberModal && (
        <AddFamilyMember
          familyId={familyId}
          onSuccess={() => {
            setShowAddMemberModal(false);
            void queryClient.invalidateQueries({ queryKey: ['members', familyId] });
          }}
          onClose={() => setShowAddMemberModal(false)}
        />
      )}

      {/* Enable Login modal */}
      {enableLoginMember && (
        <EnableLoginModal
          familyId={familyId}
          member={enableLoginMember}
          onSuccess={() => setEnableLoginMember(null)}
          onClose={() => setEnableLoginMember(null)}
        />
      )}

      {/* Confirm disable login */}
      {disableLoginMember && (
        <Modal
          isOpen
          onClose={() => setDisableLoginMember(null)}
          title="Disable Login"
          size="sm"
        >
          <p className="text-sm text-slate-600 mb-4">
            Remove login access for{' '}
            <strong>{disableLoginMember.displayName}</strong>? They will no
            longer be able to sign in with their username and PIN.
          </p>
          <div className="flex gap-2">
            <Button
              variant="danger"
              isLoading={disableLoginMutation.isPending}
              onClick={() =>
                disableLoginMutation.mutate(disableLoginMember.memberId)
              }
              fullWidth
            >
              Disable Login
            </Button>
            <Button
              variant="ghost"
              onClick={() => setDisableLoginMember(null)}
              fullWidth
            >
              Cancel
            </Button>
          </div>
        </Modal>
      )}

      {/* Confirm remove member */}
      {removeMemberId && (
        <Modal
          isOpen
          onClose={() => setRemoveMemberId(null)}
          title="Remove Member"
          size="sm"
        >
          <p className="text-sm text-slate-600 mb-4">
            Are you sure you want to remove this member? This action cannot be
            undone.
          </p>
          <div className="flex gap-2">
            <Button
              variant="danger"
              isLoading={removeMemberMutation.isPending}
              onClick={() => removeMemberMutation.mutate(removeMemberId)}
              fullWidth
            >
              Remove
            </Button>
            <Button
              variant="ghost"
              onClick={() => setRemoveMemberId(null)}
              fullWidth
            >
              Cancel
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}
